import { defineComponent as _defineComponent } from 'vue'
import CountriesRepository from '@/js/common/Domain/Repository/Countries';
import abTests from '@/js/common/Domain/Service/abTests';
import {
    SiteHeader,
    SearchForm,
    DepartureCity,
    Selection,
    NightsRange,
    DateRange,
    TouristsSelection,
    type Country,
    type Resort,
    type TouristRoom,
    useStickyOnScroll,
    DestinationPrices,
    type SearchFormCriteria,
    OfficeTourButton,
 type SearchFormExpose } from '@travelata-libs/travelata-frontend';
 import { format } from 'date-fns';
import {
    ref, computed,
    unref,
} from 'vue';
import { useState } from 'vuex-composition-helpers';
import { type IMapForSearchFormState, } from '@ve/pages/serp/store';
import { NAMESPACE as MAP_FOR_SEARCH_FORM,  } from '@ve/pages/serp/store/SearchFormStore';

interface LibSearchFormHostProps {
    disabledDestination?: boolean;
    authData: { user: { email: null | string, name: null | string }, accountUrl: string }
}
interface LibSearchFormHostEmits {
    (event: 'update:modelValue', data: any): void;
    (event: 'logout'): void;
    (event: 'login'): void;
    (event: 'search'): void;
    (event: 'buy'): void;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'LibSearchFormHost',
  props: {
    disabledDestination: { type: Boolean },
    authData: null
  },
  emits: ["update:modelValue", "logout", "login", "search", "buy"],
  setup(__props: any, { expose, emit }: { emit: ({
    (event: 'update:modelValue', data: any): void;
    (event: 'logout'): void;
    (event: 'login'): void;
    (event: 'search'): void;
    (event: 'buy'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
    disabledDestination?: boolean;
    authData: { user: { email: null | string, name: null | string }, accountUrl: string }
}



const searchFormRef = ref<SearchFormExpose & { $el: HTMLElement } | null>(null);

const {
    checkInDateRange, 
    countries,
    departureCity,
    nightRange,
    resorts,
    touristGroup,
    hotels,
} = useState<IMapForSearchFormState>(MAP_FOR_SEARCH_FORM, ['checkInDateRange', 'countries', 'departureCity', 'nightRange', 'resorts', 'touristGroup', 'hotels'])

const headerEl = ref<HTMLElement | null>(null);
const destinationPrices = ref<DestinationPrices>({ country: {}, resort: {} });
const { collapsed, progress } = useStickyOnScroll({ target: headerEl });

CountriesRepository.repository.on('minPricesLoaded', function({ countries, resorts }: { countries: Backbone.Model<Country>[], resorts: Backbone.Model<Resort>[] }) {
    countries.forEach((model: Backbone.Model<Country>) => {
        destinationPrices.value.country![model.get('id')] = model.get('minPrice') || 0;
    });
    resorts.forEach((model: Backbone.Model<Resort>) => {
        destinationPrices.value.resort![model.get('id')] = model.get('minPrice') || 0;
    });
    destinationPrices.value = { ...destinationPrices.value };
});


const isCollapsed = computed(() => collapsed.value);

const getNightRange = (data: IMapForSearchFormState['nightRange']): SearchFormCriteria['nights'] => {
    return {
        from: data?.from || 0,
        to: data?.to || 0,
    }
}

const getCheckInDateRange = (data: IMapForSearchFormState['checkInDateRange']): SearchFormCriteria['dateFrom'] => {
    return {
        from: format(data?.from, 'yyyy-MM-dd'),
        to: format(data?.to, 'yyyy-MM-dd'),
        daysFlexible: data?.daysFlexible || 0,
    }
}
const getDepartureCity = (data: IMapForSearchFormState['departureCity']): SearchFormCriteria['fromCity'] => data;

const getTouristsGroup = (data: IMapForSearchFormState['touristGroup']): SearchFormCriteria['tourists'] => {
    const all = [data, ...data?.touristsByRooms || []];
    return { rooms: all.map((t) => ({ adults: t?.adults || 0, childrenAges: (t?.ages || []) }))};
}

const adaptResorts = (resorts: IMapForSearchFormState['resorts'], country: Country): Resort[] => {
    return resorts?.map((r) => ({
        id: r.id,
        name: r.name,
        isPopular: r.isPopular,
        minPrice: r.minPrice,
        countryId: country.id,
        countryName: country.name,
        atFiltering: r.atFiltering,
    }))
};

const getDestination = (_countries: IMapForSearchFormState['countries'], _resorts: IMapForSearchFormState['resorts'], _hotels: IMapForSearchFormState['hotels']): SearchFormCriteria['destination'] => {
    const country: Country & { resorts: IMapForSearchFormState['resorts'] } = _countries[0];
    if(!country) return null;
    if (_hotels.length > 0) {
        return {
            kind: 'hotels',
            hotels: _hotels.map((h) => ({ id: h.id, name: h.name, countryId: country.id, countryName: country.name })),
            country: { ...country, resorts: adaptResorts(country.resorts, country) },
        }
    }
    const libResorts: Resort[] = adaptResorts(_resorts, country);
    if(libResorts.length > 0) {
        return {
            kind: 'resorts',
            country: { ...country, resorts: adaptResorts(country.resorts, country) },
            resorts: libResorts,
        }
    };
    return {
        kind: 'country',
        country: { ...country, resorts: adaptResorts(country.resorts, country), },
    }
}

function getUpdateFromCity(city: DepartureCity | null): Partial<IMapForSearchFormState> | null {
    if (!city) return null;           
    return {
        departureCity: {
            id: city.id,
            name: city.name,
            countriesWithDirectFlights: city.countriesWithDirectFlights ?? [],
        }
    }
}
function getUpdateDestination(selection: Selection | null): Partial<IMapForSearchFormState> | null {
    if (!selection) return null;
    return {
        resorts: selection.kind === 'resorts' ? selection.resorts as any : [],
        countries: selection.kind !== 'none' ? [selection.country] : [] as any,
        hotels: selection.kind === 'hotels' ? selection.hotels.map((h) => ({ id: h.id, name: h.name, country: h.countryId })) : [],

    }
};

function getUpdateNights(range: NightsRange | null): Partial<IMapForSearchFormState> | null {
    return {
        nightRange: {
            from: range?.from || 0,
            to: range?.to || 0,
        }
    }
}
function getUpdateCheckInDate(value: DateRange | null): Partial<IMapForSearchFormState> {
    return {
        checkInDateRange: {
            from: new Date(value?.from || ''),
            to: new Date(value?.to || ''),
            daysFlexible: value?.daysFlexible,
        }
    }
}
const adaptTouristsGroup = (t: TouristRoom) => ({
    adults: t.adults,
    kids: t.childrenAges.filter((age) => age > 1).length,
    infants:  t.childrenAges.filter((age) => age <= 1).length,
    ages: t.childrenAges,
    touristsByRooms: [],
})
function getUpdatedTourists(tourists: TouristsSelection | null): Partial<IMapForSearchFormState> | null {
    if (!tourists) return null;
    const [first, ...other] = tourists.rooms;
    if(!first) return null;

    return {
        touristGroup: {
            ...adaptTouristsGroup(first),
            touristsByRooms: other.map(adaptTouristsGroup),
        }
    }
}


const model = computed<any>({
    get: () => {
        return {
            fromCity: getDepartureCity(departureCity.value),
            destination: getDestination(countries.value, resorts.value, hotels.value),
            nights: getNightRange(nightRange.value),
            dateRange: getCheckInDateRange(checkInDateRange.value),
            tourists: getTouristsGroup(touristGroup.value),
        }
    },
    set: (data: SearchFormCriteria) => {
        emit('update:modelValue', {
            ...getUpdateFromCity(data.fromCity),
            ...getUpdateCheckInDate(data.dateRange),
            ...getUpdateDestination(data.destination),
            ...getUpdateNights(data.nights),
            ...getUpdatedTourists(data.tourists),
        })
    },
})

function onSearch(): void {
    emit('search');
}
function onBuy(): void {
    emit('buy');
}
expose({
    validate: (): boolean => (searchFormRef.value ? searchFormRef.value.validate() : true),
});

return { __sfc: true,props, emit, searchFormRef, checkInDateRange, countries, departureCity, nightRange, resorts, touristGroup, hotels, headerEl, destinationPrices, collapsed, progress, isCollapsed, getNightRange, getCheckInDateRange, getDepartureCity, getTouristsGroup, adaptResorts, getDestination, getUpdateFromCity, getUpdateDestination, getUpdateNights, getUpdateCheckInDate, adaptTouristsGroup, getUpdatedTourists, model, onSearch, onBuy, abTests, SiteHeader, SearchForm, OfficeTourButton }
}

})